import type { Edge } from '@xyflow/react'
import type { MoveDrop, MoveScope } from 'app/types/workflow-graph-edits'
import { ObjectId } from 'lib/ObjectId'
import { collectDownstream, forkHandles, type StepOf } from './autoLayout'
import { bridgeEdges } from './graphUtils'
import type { WorkflowNode } from 'app/types/workflows'

type GraphNode = Pick<WorkflowNode, `id`> & StepOf

/** The step's first outlet with nothing attached: a fork arm id, `null` for a plain step's single
 *  outlet, or undefined when every outlet is taken (or, for an Exit, there is none). */
function openOutlet(node: GraphNode, edges: readonly Edge[]): string | null | undefined {
  const outlets = node.type === `exit` ? [] : (forkHandles(node) ?? [null])
  return outlets.find(
    (handle) => !edges.some((edge) => edge.source === node.id && (edge.sourceHandle ?? null) === handle),
  )
}

/** Detaches a step from its parent. A plain step also lets go of its child, and the parent is
 *  bridged to that child; a fork keeps its arms, which travel with it. */
function lift(node: GraphNode, edges: readonly Edge[]): Edge[] {
  if (forkHandles(node)) return edges.filter((edge) => edge.target !== node.id)
  const bridges = bridgeEdges(node.id, edges).map(
    (bridge): Edge => ({ id: ObjectId(), ...bridge, type: `connector`, animated: true }),
  )
  return [...edges.filter((edge) => edge.source !== node.id && edge.target !== node.id), ...bridges]
}

function place(node: GraphNode, drop: MoveDrop, edges: Edge[]): Edge[] {
  if (drop.kind === `end`) {
    return [
      ...edges,
      {
        id: ObjectId(),
        source: drop.source,
        sourceHandle: drop.sourceHandle ?? undefined,
        target: node.id,
        type: `connector`,
        animated: true,
      },
    ]
  }
  const target = edges.find((edge) => edge.id === drop.edgeId)
  const outlet = openOutlet(node, edges)
  if (!target || outlet === undefined) return edges
  return [
    ...edges.filter((edge) => edge.id !== target.id),
    { ...target, id: ObjectId(), target: node.id },
    {
      id: ObjectId(),
      source: node.id,
      sourceHandle: outlet ?? undefined,
      target: target.target,
      type: `connector`,
      animated: true,
    },
  ]
}

export function moveScope(node: GraphNode, edges: readonly Edge[]): MoveScope {
  return {
    nodeId: node.id,
    travelling: forkHandles(node) ? collectDownstream(node.id, edges) : new Set([node.id]),
    spliceable: openOutlet(node, lift(node, edges)) !== undefined,
  }
}

export function moveNode(node: GraphNode, drop: MoveDrop, edges: Edge[]): Edge[] {
  if (drop.kind === `edge` && !edges.some((edge) => edge.id === drop.edgeId)) return edges
  return place(node, drop, lift(node, edges))
}

/** Wires a fresh copy of `original` in right below it. A fork's copy can't sit below without
 *  taking over one of its arms, so it takes the original's slot and the original hangs off it. */
export function duplicateEdges(original: GraphNode, copyId: string, edges: Edge[]): Edge[] {
  // The copy has the original's data, so its exits are the original's.
  const copy = { ...original, id: copyId }
  if (forkHandles(original)) {
    return place(
      original,
      { kind: `end`, source: copyId, sourceHandle: openOutlet(copy, []) ?? null },
      edges.map((edge) => (edge.target === original.id ? { ...edge, target: copyId } : edge)),
    )
  }
  const outgoing = edges.find((edge) => edge.source === original.id)
  return outgoing
    ? place(copy, { kind: `edge`, edgeId: outgoing.id }, edges)
    : place(copy, { kind: `end`, source: original.id, sourceHandle: null }, edges)
}
